import { useEffect, useRef, useState } from "react";

interface VoiceInputButtonProps {
  initialValue?: string;
  onSave?: (value: string) => void;
  className?: string;
  size?: "sm" | "md";
}

export function VoiceInputButton({
  initialValue = "",
  onSave,
  className = "",
  size = "md"
}: VoiceInputButtonProps): JSX.Element {
  const recognitionRef = useRef<BrowserSpeechRecognition | null>(null);
  const valueRef = useRef(initialValue);
  const [listening, setListening] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    valueRef.current = initialValue;
  }, [initialValue]);

  useEffect(() => {
    return () => recognitionRef.current?.abort();
  }, []);

  const handleClick = () => {
    if (listening) {
      recognitionRef.current?.stop();
      return;
    }

    const Recognition = getSpeechRecognitionConstructor();
    if (!Recognition) {
      setError("当前浏览器不支持语音识别，请使用最新版 Chrome 或 Edge。");
      return;
    }

    const recognition = new Recognition();
    recognition.lang = "zh-CN";
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onstart = () => {
      setError(null);
      setListening(true);
    };
    recognition.onresult = (event) => {
      let transcript = "";
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        if (event.results[index].isFinal) {
          transcript += event.results[index][0]?.transcript ?? "";
        }
      }
      const recognizedText = transcript.trim();
      if (recognizedText) {
        const currentText = valueRef.current.trimEnd();
        onSave?.(currentText ? `${currentText}\n${recognizedText}` : recognizedText);
      }
    };
    recognition.onerror = (event) => {
      setError(formatSpeechError(event.error));
    };
    recognition.onend = () => {
      setListening(false);
      recognitionRef.current = null;
    };
    recognitionRef.current = recognition;

    try {
      recognition.start();
    } catch {
      recognitionRef.current = null;
      setListening(false);
      setError("语音识别暂时无法启动，请稍后重试。");
    }
  };

  const buttonSizeClass = size === "sm" ? "h-8 w-8 text-xs" : "h-9 w-9 text-sm";

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`inline-flex items-center justify-center rounded-full border bg-white shadow-sm transition ${
        error
          ? "border-rose-300 text-rose-600 hover:bg-rose-50"
          : listening
            ? "animate-pulse border-brand bg-brand/10 text-brand"
            : "border-slate-200 text-brand hover:border-brand hover:bg-brand/5"
      } ${buttonSizeClass} ${className}`}
      aria-label={listening ? "停止语音输入" : "开始语音输入"}
      aria-pressed={listening}
      title={error ?? (listening ? "正在识别，点击停止" : "语音输入（中文）")}
    >
      <MicIcon className="h-4 w-4" />
    </button>
  );
}

interface BrowserSpeechRecognitionResult {
  readonly isFinal: boolean;
  readonly length: number;
  readonly [index: number]: { readonly transcript: string } | undefined;
}

interface BrowserSpeechRecognitionEvent {
  readonly resultIndex: number;
  readonly results: {
    readonly length: number;
    readonly [index: number]: BrowserSpeechRecognitionResult;
  };
}

interface BrowserSpeechRecognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  onstart: (() => void) | null;
  onresult: ((event: BrowserSpeechRecognitionEvent) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

type BrowserSpeechRecognitionConstructor = new () => BrowserSpeechRecognition;

function getSpeechRecognitionConstructor(): BrowserSpeechRecognitionConstructor | undefined {
  const speechWindow = window as typeof window & {
    SpeechRecognition?: BrowserSpeechRecognitionConstructor;
    webkitSpeechRecognition?: BrowserSpeechRecognitionConstructor;
  };
  return speechWindow.SpeechRecognition ?? speechWindow.webkitSpeechRecognition;
}

function formatSpeechError(error: string): string {
  if (error === "not-allowed" || error === "service-not-allowed") {
    return "未获得麦克风权限，请在浏览器设置中允许后重试。";
  }
  if (error === "no-speech") {
    return "没有识别到语音，请靠近麦克风后重试。";
  }
  if (error === "audio-capture") {
    return "未检测到可用麦克风。";
  }
  return "语音识别失败，请稍后重试；原有文字未受影响。";
}

function MicIcon({ className = "" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 16a4 4 0 0 0 4-4V6a4 4 0 0 0-8 0v6a4 4 0 0 0 4 4z" />
      <path d="M19 10a7 7 0 0 1-14 0" />
      <path d="M12 19v4" />
      <path d="M8 23h8" />
    </svg>
  );
}
